/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2017, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import PropTypes from 'prop-types';

import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { DragSource as dragSource, DropTarget as dropTarget } from 'react-dnd';
import { omit, uniqueId } from 'lodash';

export class SortableDragLayer extends Component<any, any> {
	static propTypes = {
		itemProps: PropTypes.object.isRequired,
		currentOffset: PropTypes.object.isRequired,
	};

	componentDidMount() {
		this._div.appendChild(this.props.itemProps.node);
	}

	_div: HTMLElement;

	_ref = div => {
		this._div = div;
	};

	render() {
		const { currentOffset, itemProps } = this.props;
		const { x, y } = currentOffset;
		const transform = `translate(${x}px, ${y}px)`;
		const { boundingRect } = itemProps;

		return (
			<div
				ref={this._ref}
				style={{ transform, width: boundingRect.width, height: boundingRect.height }}
			/>
		);
	}
}

export default function sortableItem({
	dropTargetMethods = {},
	dragSourceMethods = {},
	type = uniqueId('SORTABLE_ITEM'),
}: {
	dropTargetMethods: {
		hover?: (props, monitor, component) => void;
	};
	dragSourceMethods: {
		beginDrag?: (
			props,
			monitor,
			component
		) => {
			node?: HTMLElement;
			dragLayerRenderer: (any) => any;
		};
	};
	type: string;
}) {
	const sortableHoc = function(Item) {
		const hover = function(props, monitor, component) {
			const dragIndex = monitor.getItem().index;
			const hoverIndex = props.index;

			if (dragIndex === hoverIndex) {
				return;
			}

			const hoverBoundingRect = (ReactDOM.findDOMNode(
				component
			) as HTMLElement).getBoundingClientRect();
			const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
			const clientOffset = monitor.getClientOffset();
			const hoverClientY = clientOffset.y - hoverBoundingRect.top;

			// Dragging downwards
			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
				return;
			}

			// Dragging upwards
			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
				return;
			}
			props.moveItem(dragIndex, hoverIndex);
			monitor.getItem().index = hoverIndex;
			dropTargetMethods.hover && dropTargetMethods.hover(props, monitor, component);
		};

		const beginDrag = function(props, monitor, component) {
			const node = ReactDOM.findDOMNode(component) as HTMLElement;
			const boundingRect = node.getBoundingClientRect();
			const dragProps: {
				index: number;
				boundingRect: DOMRect;
				node?: HTMLElement;
				dragLayerRenderer?: (any) => any;
			} = { index: props.index, boundingRect };

			if (dragSourceMethods.beginDrag) {
				Object.assign(dragProps, dragSourceMethods.beginDrag(props, monitor, component));
			}

			if (!dragProps.dragLayerRenderer) {
				dragProps.node = node.cloneNode(true) as HTMLElement;
				dragProps.dragLayerRenderer = props => <SortableDragLayer {...props} />;
			}

			return dragProps;
		};

		@dropTarget(type, { ...dropTargetMethods, hover }, (connect, monitor) => ({
			connectDropTarget: connect.dropTarget(),
			isOver: monitor.isOver(),
		}))
		@dragSource(type, { ...dragSourceMethods, beginDrag }, (connect, monitor) => ({
			connectDragSource: connect.dragSource(),
			isDragging: monitor.isDragging(),
		}))
		class SortableItem extends Component<any, any> {
			static propTypes = {
				index: PropTypes.number,
				length: PropTypes.number,
				moveItem: PropTypes.func,
				connectDragSource: PropTypes.func,
				connectDropTarget: PropTypes.func,
			};

			componentDidMount() {
				this._node.addEventListener('keydown', this._onKeyDown);
			}

			componentWillUnmount() {
				this._node.removeEventListener('keydown', this._onKeyDown);
			}

			_node: HTMLElement;

			_onKeyDown = e => {
				const { keyCode, altKey, target } = e;
				const { index, length, moveItem } = this.props;
				let moved = false;

				if (length != null) {
					if (keyCode === 38 && index > 0) {
						// up arrow
						moveItem(index, altKey ? 0 : index - 1);
						moved = true;
					} else if (keyCode === 40 && index !== length - 1) {
						// down arrow
						moveItem(index, altKey ? length - 1 : index + 1);
						moved = true;
					}

					if (moved) {
						e.preventDefault();
						target.scrollIntoView(false);
					}
				}
			};
			_connectToDnD = ref => {
				const { connectDragSource, connectDropTarget } = this.props;
				const node = ReactDOM.findDOMNode(ref) as HTMLElement;

				this._node = node;
				connectDropTarget(node);
				connectDragSource(node, { restrictToYAxis: true });
			};
			render() {
				const passThroughProps = omit(this.props, 'connectDragSource', 'connectDropTarget');

				return <Item {...passThroughProps} ref={this._connectToDnD} />;
			}
		}

		return SortableItem as any;
	};

	return typeof arguments[0] === 'object' ? sortableHoc : sortableHoc(arguments[0]);
}
